/* eslint no-console: ["error", { allow: ["error", "log"] }] */

import React, { useState, useEffect } from 'react';
import SalesFigures from '../../components/SalesFigures/';
import { useTranslation } from 'react-i18next';
import '../../../i18n';
import SelectedNews from 'scripts/components/News/SelectedNews';
import DeliveryLists from 'scripts/components/DeliveryLists';
import BasicDataTable from 'scripts/components/DataTable/BasicDataTable';
import { formatDate } from '../../components/Utils/dates';
import { useMsal, useAccount } from '@azure/msal-react';
import {
    InteractionStatus,
} from '@azure/msal-browser';
import useAxios from 'scripts/hooks/useAxios';
import { useGlobalState } from '../../contexts/GlobalStateContext';
import { TableLoader } from '../../components/ContentLoader';
import ErrorInfo from '../../components/Messages';
import { convertToLocale, convertMarketToCurrency } from '../../components/Utils/market';
import { formatNumber } from 'scripts/components/Utils/numbers';

function Home() {
    const { accounts, inProgress } = useMsal();
    const { callApi } = useAxios();
    const { globalState } = useGlobalState();

    const { t } = useTranslation();

    const accountInfo = useAccount(accounts[0] || {});

    const name = accountInfo?.idTokenClaims.given_name; // eslint-disable-line camelcase
    const isAdmin = globalState?.activeUserCompany?.roles?.toLowerCase().includes('portal admin') ?? false;
    const custMarket = globalState?.activeUserCompany?.market?.toLowerCase().trim() ?? "";

    const [orders, setOrders] = useState(null);
    const [ordersLoading, setOrdersLoading] = useState(true);
    const [error, setError] = useState(false)
    const [locale, setLocale] = useState("sv-SE");
    const [currency, setCurrency] = useState("SEK");

    useEffect(() => {
        if (globalState?.activeUserCompany === null) return;
        const custMarket = globalState?.activeUserCompany?.market?.toLowerCase().trim() ?? "";
        setCurrency(convertMarketToCurrency(custMarket));
        setLocale(convertToLocale(custMarket));
        setOrdersLoading(true);
        setOrders(null);
        setError(false);
    }, [globalState?.activeUserCompany]);

    useEffect(() => {
        if (!orders && inProgress === InteractionStatus.None && globalState?.activeUserCompany) {
            callApi({
                method: 'get',
                url: '/api/order/historyOrders?amount=3',
                headers: {
                    'x-active-user-company': globalState.activeUserCompany?.contactId,
                },
            }).then(
                (res) => {
                    if (res.status === 200) {
                        setOrders(res.data.Orders);
                        setOrdersLoading(false);
                    }
                },
                [orders, ordersLoading, inProgress, callApi]
            ).catch((error) => {
                setError(true)
                console.error(error); // eslint-disable-line
            });
        }
    }, [callApi, globalState.activeUserCompany, inProgress, orders, ordersLoading]);

    const columns = React.useMemo(
        () => [
            {
                Header: t('orders.header.order-number'),
                accessor: 'XAOrderNbr',
            },
            {
                Header: t('orders.header.date'),
                accessor: 'OrderDate',
                Cell: ({ value }) => {
                    return formatDate(String(value));
                },
            },
            {
                Header: t('orders.header.tracking-id'),
                accessor: 'TrackingID',
                Cell: function Tracking(cell) {
                    return (
                        <a
                            href={cell.row.original.TrackingURL}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            {cell.value}
                        </a>
                    );
                },
            },
            {
                Header: t('orders.header.order-mark'),
                accessor: 'OrderMark',
            },
            {
                Header: t('orders.header.reference'),
                accessor: 'OrderRef',
            },
            {
                Header: t('orders.header.dispatch-date'),
                accessor: 'PromiseWeek',
            },
            {
                Header: t('orders.header.sum'),
                accessor: 'OrderValue',
                Cell: (cell) => {
                    return formatNumber(cell.value, currency, locale)
                },
            },
        ],
        [currency, locale, t]
    );

    return (
        <div className="main-content">
            <h1>{t('home.welcome-title', { name: name })}</h1>
            <SelectedNews market={custMarket} />
            {isAdmin && <SalesFigures />}

            <div className="orders">
                <h2>{t('home.recent-orders-title')}</h2>
                {ordersLoading && !error &&
                    <TableLoader />
                }
                {orders &&
                    <BasicDataTable
                        columns={columns}
                        data={orders}
                        currency={currency}
                        locale={locale}
                        className="table-boxed"
                        limit="2"
                    />
                }
                {error &&
                    <ErrorInfo text={t('error.title.orders')} />
                }
            </div>

            {custMarket === "se" && <DeliveryLists />}
        </div>
    );
}

export default Home;
